import Form from "@rjsf/core";
import type { RJSFSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cable, Plus, Search } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { queryString, request } from "../api";
import { useAuth } from "../auth";
import {
  ConfirmForm,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  Panel,
  StatusPill,
} from "../components";
import { formatTime } from "../format";
import type {
  Connection,
  ConnectorManifest,
  Tenant,
} from "../types";

export function TenantsPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [registering, setRegistering] = useState(false);
  const tenants = useQuery({
    queryKey: ["tenants", search],
    queryFn: () => request<Tenant[]>(`/tenants${queryString({ search, limit: 200 })}`),
  });

  const canOperate = session?.user.role !== "viewer";

  return (
    <>
      <PageHeader
        eyebrow="Tenant directory"
        title="Nexus footprint by tenant"
        description="Names, notes, and tags live only in Nexus. Lueira remains the tenant authority."
        actions={canOperate && (
          <button type="button" className="button button--primary" onClick={() => setRegistering(true)}>
            <Plus size={16} /> Register tenant
          </button>
        )}
      />
      <Panel>
        <label className="search-field">
          <Search size={17} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tenant ID or local name"
          />
        </label>
        {tenants.isLoading && <LoadingState />}
        {tenants.error && <ErrorState error={tenants.error} />}
        {tenants.data && tenants.data.length > 0 ? (
          <div className="tenant-grid">
            {tenants.data.map((tenant) => {
              const title = tenant.registered_name ?? tenant.display_name ?? tenant.tenant_id;

              return (
                <Link className="tenant-card" to={`/tenants/${tenant.tenant_id}`} key={tenant.tenant_id}>
                  <div className="tenant-card__topline">
                    <span className="tenant-card__monogram">
                      {title.slice(0, 2).toUpperCase()}
                    </span>
                    {tenant.failure_count > 0
                      ? <StatusPill value="degraded" />
                      : <StatusPill value="healthy" />}
                  </div>
                  <div>
                    <h2>{title}</h2>
                    <code>{tenant.tenant_id}</code>
                  </div>
                  <dl>
                    <div><dt>Connections</dt><dd>{tenant.active_count}/{tenant.connection_count}</dd></div>
                    <div><dt>Failures / 24h</dt><dd>{tenant.failure_count}</dd></div>
                    <div><dt>Last activity</dt><dd>{formatTime(tenant.last_activity_at)}</dd></div>
                  </dl>
                  {(tenant.tags ?? []).length > 0 && (
                    <div className="tag-row">
                      {(tenant.tags ?? []).map((tag) => <span key={tag}>{tag}</span>)}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        ) : !tenants.isLoading && (
          <EmptyState
            title="No tenants match this view"
            body="Tenants appear after Nexus observes a connection or an incoming event, or once registered manually."
          />
        )}
      </Panel>
      {registering && (
        <RegisterTenantModal
          onClose={() => setRegistering(false)}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ["tenants"] });
            setRegistering(false);
          }}
        />
      )}
    </>
  );
}

function RegisterTenantModal({
  onClose,
  onSaved,
}: {
  onClose(): void;
  onSaved(): Promise<void>;
}) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await request("/tenants", {
        method: "POST",
        body: JSON.stringify({ id, name, reason }),
      });
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Tenant could not be registered.");
    }
  }

  return (
    <Modal title="Register tenant" onClose={onClose}>
      <form className="stack-form" onSubmit={(event) => void submit(event)}>
        <label>Tenant ID<input required value={id} onChange={(event) => setId(event.target.value)} /></label>
        <label>Name<input required value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        {error && <div className="inline-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="button button--primary">Register tenant</button>
        </div>
      </form>
    </Modal>
  );
}

export function TenantPage() {
  const { tenantId = "" } = useParams();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [editingProfile, setEditingProfile] = useState(false);
  const [creatingConnection, setCreatingConnection] = useState(false);
  const [control, setControl] = useState<{ connection: Connection; active: boolean }>();
  const [rotatingCredentials, setRotatingCredentials] = useState<Connection>();
  const [managingBindings, setManagingBindings] = useState<Connection>();

  const tenant = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => request<Tenant>(`/tenants/${encodeURIComponent(tenantId)}`),
  });
  const connections = useQuery({
    queryKey: ["connections", tenantId],
    queryFn: () => request<Connection[]>(`/tenants/${encodeURIComponent(tenantId)}/connections`),
  });

  if (tenant.isLoading || connections.isLoading) {
    return <LoadingState />;
  }
  if (tenant.error || connections.error || !tenant.data) {
    return <ErrorState error={tenant.error ?? connections.error} />;
  }

  const canOperate = session?.user.role !== "viewer";

  async function setState(reason: string) {
    if (!control) return;
    await request(
      `/tenants/${encodeURIComponent(tenantId)}/connections/${control.connection.id}/${control.active ? "enable" : "disable"}`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
    await queryClient.invalidateQueries({ queryKey: ["connections", tenantId] });
    await queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
    setControl(undefined);
  }

  return (
    <>
      <PageHeader
        eyebrow={`Tenant / ${tenant.data.tenant_id}`}
        title={tenant.data.registered_name ?? tenant.data.display_name ?? "Unnamed tenant"}
        description={tenant.data.notes ?? "No local operational notes have been added."}
        actions={(
          <>
            {canOperate && (
              <button type="button" className="button button--secondary" onClick={() => setEditingProfile(true)}>
                Edit annotations
              </button>
            )}
            {canOperate && (
              <button type="button" className="button button--primary" onClick={() => setCreatingConnection(true)}>
                <Plus size={16} /> Add integration
              </button>
            )}
          </>
        )}
      />

      <section className="metric-strip metric-strip--compact">
        <div className="metric"><span>Connections</span><strong>{tenant.data.connection_count}</strong></div>
        <div className="metric metric--positive"><span>Active</span><strong>{tenant.data.active_count}</strong></div>
        <div className={`metric ${tenant.data.failure_count ? "metric--negative" : ""}`}>
          <span>Failures / 24h</span><strong>{tenant.data.failure_count}</strong>
        </div>
        <div className="metric"><span>Last activity</span><strong className="metric__time">{formatTime(tenant.data.last_activity_at)}</strong></div>
        <div className="metric">
          <span>Registered</span>
          <strong className="metric__time">
            {tenant.data.registered_name
              ? `${tenant.data.registered_name} · ${formatTime(tenant.data.registered_at)}`
              : "Not registered"}
          </strong>
        </div>
      </section>

      <Panel title="Integrations">
        {connections.data && connections.data.length > 0 ? (
          <div className="connection-list">
            {connections.data.map((connection) => (
              <article className="connection-row" key={connection.id}>
                <div className="connection-row__icon"><Cable size={19} /></div>
                <div className="connection-row__identity">
                  <strong>{connection.name}</strong>
                  <span>{connection.connector_id}</span>
                </div>
                <StatusPill value={connection.state} />
                <div className="connection-row__health">
                  <small>Health</small>
                  <span>{connection.health}</span>
                </div>
                <div className="connection-row__health">
                  <small>Last sync</small>
                  <span>{formatTime(connection.last_synced_at)}</span>
                </div>
                {canOperate && (
                  <div className="connection-row__actions">
                    <button
                      type="button"
                      className="button button--small button--secondary"
                      onClick={() => setRotatingCredentials(connection)}
                    >
                      Rotate credentials
                    </button>
                    {connection.connector_id === "picnegre" && (
                      <button
                        type="button"
                        className="button button--small button--secondary"
                        onClick={() => setManagingBindings(connection)}
                      >
                        Store bindings
                      </button>
                    )}
                    <button
                      type="button"
                      className="button button--small button--secondary"
                      onClick={() => setControl({
                        connection,
                        active: connection.state !== "active",
                      })}
                    >
                      {connection.state === "active" ? "Disable" : "Enable"}
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No integrations configured"
            body="Add a connector to begin routing tenant data to a provider."
          />
        )}
      </Panel>

      {editingProfile && (
        <ProfileModal
          tenant={tenant.data}
          onClose={() => setEditingProfile(false)}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
            setEditingProfile(false);
          }}
        />
      )}
      {creatingConnection && (
        <ConnectionModal
          tenantId={tenantId}
          onClose={() => setCreatingConnection(false)}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ["connections", tenantId] });
            await queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
            setCreatingConnection(false);
          }}
        />
      )}
      {control && (
        <Modal
          title={`${control.active ? "Enable" : "Disable"} ${control.connection.name}`}
          onClose={() => setControl(undefined)}
        >
          <ConfirmForm
            action={control.active ? "enable this integration" : "disable this integration"}
            detail={control.active
              ? "New matching events will be routed to this provider."
              : "New matching events will no longer be delivered to this provider."}
            confirmLabel={control.active ? "Enable integration" : "Disable integration"}
            onConfirm={setState}
            onCancel={() => setControl(undefined)}
          />
        </Modal>
      )}
      {rotatingCredentials && (
        <RotateCredentialsModal
          tenantId={tenantId}
          connection={rotatingCredentials}
          onClose={() => setRotatingCredentials(undefined)}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ["connections", tenantId] });
            setRotatingCredentials(undefined);
          }}
        />
      )}
      {managingBindings && (
        <PicnegreStoreBindingsModal
          tenantId={tenantId}
          connection={managingBindings}
          onClose={() => setManagingBindings(undefined)}
        />
      )}
    </>
  );
}

function ProfileModal({
  tenant,
  onClose,
  onSaved,
}: {
  tenant: Tenant;
  onClose(): void;
  onSaved(): Promise<void>;
}) {
  const [name, setName] = useState(tenant.display_name ?? "");
  const [notes, setNotes] = useState(tenant.notes ?? "");
  const [tags, setTags] = useState(() => (tenant.tags ?? []).join(", "));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await request(`/tenants/${encodeURIComponent(tenant.tenant_id)}`, {
        method: "PATCH",
        body: JSON.stringify({
          display_name: name || null,
          notes: notes || null,
          tags: tags.split(",").map((tag) => tag.trim()).filter(Boolean),
          reason,
        }),
      });
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Annotations could not be saved.");
    }
  }

  return (
    <Modal title="Edit tenant annotations" onClose={onClose}>
      <form className="stack-form" onSubmit={(event) => void submit(event)}>
        <label>Display name<input value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Notes<textarea value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        <label>Tags<input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="production, priority" /></label>
        <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        {error && <div className="inline-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="button button--primary">Save annotations</button>
        </div>
      </form>
    </Modal>
  );
}

function ConnectionModal({
  tenantId,
  onClose,
  onSaved,
}: {
  tenantId: string;
  onClose(): void;
  onSaved(): Promise<void>;
}) {
  const manifests = useQuery({
    queryKey: ["connectors"],
    queryFn: () => request<ConnectorManifest[]>("/connectors"),
  });
  const [connectorId, setConnectorId] = useState("");
  const [name, setName] = useState("");
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [credentials, setCredentials] = useState<Record<string, unknown>>({});
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const manifest = useMemo(
    () => manifests.data?.find((item) => item.id === connectorId),
    [connectorId, manifests.data],
  );

  async function submit() {
    if (!manifest) return;
    try {
      const connection = await request<Connection>(`/tenants/${encodeURIComponent(tenantId)}/connections`, {
        method: "POST",
        body: JSON.stringify({
          connector_id: connectorId,
          name,
          config,
          reason,
        }),
      });
      if (Object.keys(credentials).length > 0) {
        await request(`/tenants/${encodeURIComponent(tenantId)}/connections/${connection.id}/credentials`, {
          method: "PUT",
          body: JSON.stringify({ secrets: credentials, reason }),
        });
      }
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Integration could not be created.");
    }
  }

  return (
    <Modal title="Add tenant integration" onClose={onClose}>
      <div className="stack-form schema-form">
        <label>
          Connector
          <select
            required
            value={connectorId}
            onChange={(event) => {
              setConnectorId(event.target.value);
              setConfig({});
              setCredentials({});
            }}
          >
            <option value="">Choose a connector</option>
            {manifests.data?.map((item) => (
              <option value={item.id} key={item.id}>{item.display_name}</option>
            ))}
          </select>
        </label>
        <label>Connection name<input required value={name} onChange={(event) => setName(event.target.value)} /></label>
        {manifest?.config_schema && (
          <fieldset>
            <legend>Configuration</legend>
            <Form
              schema={manifest.config_schema as RJSFSchema}
              formData={config}
              validator={validator}
              onChange={(event) => setConfig(event.formData as Record<string, unknown>)}
            >
              {null}
            </Form>
          </fieldset>
        )}
        {manifest?.credential_schema && (
          <fieldset>
            <legend>Credentials</legend>
            <p className="field-help">Values are encrypted and can only be replaced, never displayed.</p>
            <Form
              schema={manifest.credential_schema as RJSFSchema}
              formData={credentials}
              validator={validator}
              onChange={(event) => setCredentials(event.formData as Record<string, unknown>)}
              uiSchema={{
                "ui:options": { label: false },
              }}
            >
              {null}
            </Form>
          </fieldset>
        )}
        <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        {error && <div className="inline-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="button button--primary"
            disabled={!manifest || !name || !reason}
            onClick={() => void submit()}
          >
            Create integration
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RotateCredentialsModal({
  tenantId,
  connection,
  onClose,
  onSaved,
}: {
  tenantId: string;
  connection: Connection;
  onClose(): void;
  onSaved(): Promise<void>;
}) {
  const manifests = useQuery({
    queryKey: ["connectors"],
    queryFn: () => request<ConnectorManifest[]>("/connectors"),
  });
  const [credentials, setCredentials] = useState<Record<string, unknown>>({});
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const manifest = useMemo(
    () => manifests.data?.find((item) => item.id === connection.connector_id),
    [connection.connector_id, manifests.data],
  );

  async function submit() {
    try {
      await request(`/tenants/${encodeURIComponent(tenantId)}/connections/${connection.id}/credentials`, {
        method: "PUT",
        body: JSON.stringify({ secrets: credentials, reason }),
      });
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Credentials could not be rotated.");
    }
  }

  return (
    <Modal title={`Rotate credentials — ${connection.name}`} onClose={onClose}>
      <div className="stack-form schema-form">
        <p className="field-help">
          Enter only the fields you want to replace. Existing secrets are never displayed here
          and are left untouched unless you provide a new value — leave a rotation twin
          (e.g. *_next) blank to leave it as-is.
        </p>
        {manifests.isLoading && <LoadingState label="Loading credential schema" />}
        {manifest?.credential_schema && (
          <fieldset>
            <legend>Credentials</legend>
            <Form
              schema={manifest.credential_schema as RJSFSchema}
              formData={credentials}
              validator={validator}
              onChange={(event) => setCredentials(event.formData as Record<string, unknown>)}
              uiSchema={{
                "ui:options": { label: false },
              }}
            >
              {null}
            </Form>
          </fieldset>
        )}
        <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        {error && <div className="inline-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="button button--ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="button button--primary"
            disabled={!reason || Object.keys(credentials).length === 0}
            onClick={() => void submit()}
          >
            Save credentials
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface PicnegreStoreBinding {
  company: string;
  store_id: string;
  connection_id: string;
  tenant_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function PicnegreStoreBindingsModal({
  tenantId,
  connection,
  onClose,
}: {
  tenantId: string;
  connection: Connection;
  onClose(): void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ["picnegre-store-bindings", tenantId, connection.id];
  const bindings = useQuery({
    queryKey,
    queryFn: () => request<PicnegreStoreBinding[]>(
      `/tenants/${encodeURIComponent(tenantId)}/connections/${connection.id}/picnegre-store-bindings`,
    ),
  });

  const [company, setCompany] = useState("ESP");
  const [storeId, setStoreId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey });
  }

  async function addBinding(event: FormEvent) {
    event.preventDefault();
    try {
      await request(
        `/tenants/${encodeURIComponent(tenantId)}/connections/${connection.id}/picnegre-store-bindings`,
        { method: "POST", body: JSON.stringify({ company, store_id: storeId, reason }) },
      );
      setStoreId("");
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Binding could not be created.");
    }
  }

  async function removeBinding(binding: PicnegreStoreBinding) {
    if (!reason) {
      setError("Enter a reason before removing a binding.");
      return;
    }
    try {
      await request(
        `/tenants/${encodeURIComponent(tenantId)}/connections/${connection.id}` +
          `/picnegre-store-bindings/${encodeURIComponent(binding.company)}/${encodeURIComponent(binding.store_id)}`,
        { method: "DELETE", body: JSON.stringify({ reason }) },
      );
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Binding could not be removed.");
    }
  }

  return (
    <Modal title={`Store bindings — ${connection.name}`} onClose={onClose}>
      <div className="stack-form">
        <p className="field-help">
          Maps a Pic Negre (company, store_id) pair — sent on every Entry call — to this
          connection, i.e. to this Lueira tenant. Each pair can be bound to only one connection.
        </p>
        {bindings.isLoading && <LoadingState label="Loading bindings" />}
        {bindings.error && <ErrorState error={bindings.error} />}
        {bindings.data && bindings.data.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr><th>Company</th><th>Store ID</th><th /></tr>
            </thead>
            <tbody>
              {bindings.data.map((binding) => (
                <tr key={`${binding.company}/${binding.store_id}`}>
                  <td>{binding.company}</td>
                  <td>{binding.store_id}</td>
                  <td>
                    <button
                      type="button"
                      className="button button--small button--secondary"
                      onClick={() => void removeBinding(binding)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : !bindings.isLoading && (
          <EmptyState title="No store bindings yet" body="Add one below." />
        )}
        <form className="stack-form" onSubmit={(event) => void addBinding(event)}>
          <label>
            Company
            <select value={company} onChange={(event) => setCompany(event.target.value)}>
              <option value="AND">AND — Andorra</option>
              <option value="ESP">ESP — Spain</option>
            </select>
          </label>
          <label>Store ID<input required value={storeId} onChange={(event) => setStoreId(event.target.value)} /></label>
          <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} /></label>
          {error && <div className="inline-error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="button button--ghost" onClick={onClose}>Close</button>
            <button type="submit" className="button button--primary" disabled={!storeId || !reason}>
              Add binding
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

